import { Translation } from '@ps/web-shared-components';
import type { MarketView, Runner } from '@ps/web-sports-api-types';
import { MarketStatus, RunnerStatusMarketDetails } from '@ps/web-sports-api-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type { PolymorphicComponentProps } from '@swf/sports/types/polymorphicComponent';
import type {
  EventBasedMarketTemplatesView,
  MeetingBasedMarketTemplatesView
} from '@swf/sports/types/templates';

import RunnerDetails from '../RunnerDetails/RunnerDetails';

import { TXTWCL_Show_More } from './SelectionList.messages.json';
import styles from './SelectionList.scss';

interface SelectionListItemProps {
  market?: MarketView;
  runner: Runner;
  eventStartTime?: string;
  showPreviousPrice?: true;
}

const SelectionListItem = <C extends React.ElementType = typeof React.Fragment>({
  as,
  className,
  eventStartTime,
  market,
  runner,
  showPreviousPrice,
  ...props
}: PolymorphicComponentProps<C, SelectionListItemProps>) => {
  if (!market || !eventStartTime) {
    return null;
  }

  const { eventId, legTypes, marketId, marketStatus } = market;

  const labelClassName = classNames(styles.label, {
    [styles.disabled]:
      marketStatus !== MarketStatus.OPEN || runner.runnerStatus !== RunnerStatusMarketDetails.ACTIVE
  });

  const ListItem = as || React.Fragment;
  const listItemProps = as
    ? { ...props, className: classNames(styles.listItem, className) }
    : props;

  return (
    <ListItem {...listItemProps}>
      <RunnerDetails
        runner={runner}
        labelClassName={labelClassName}
        showPreviousPrice={showPreviousPrice}
      >
        <Selection
          className={styles.odds}
          competitionId={market.competitionId}
          eventId={eventId}
          eventTypeId={market.eventTypeId}
          handicap={runner.handicap}
          legTypes={legTypes}
          marketId={marketId}
          marketStatus={marketStatus}
          marketType={market.marketType}
          odds={runner.winRunnerOdds}
          runnerResultType={runner.resultType}
          runnerStatus={runner.runnerStatus}
          selectionId={runner.selectionId}
          startTime={eventStartTime}
        />
      </RunnerDetails>
    </ListItem>
  );
};

const ShowMoreButton = <C extends React.ElementType = 'li'>({
  as,
  onClick
}: PolymorphicComponentProps<C, { onClick: () => void }>) => {
  const Component = as ?? 'li';

  return (
    <Component className={styles.showMoreWrapper}>
      <button type="button" className={styles.showMore} onClick={onClick}>
        <Translation {...TXTWCL_Show_More} />
      </button>
    </Component>
  );
};

const Title: React.FC<JSX.IntrinsicElements['dt']> = ({ children, ...props }) => (
  <dt {...props} className={classNames(styles.name, props.className)}>
    {children}
  </dt>
);

interface SelectionListProps {
  market: MarketView;
}

function useEventStartTime(market: MarketView) {
  const { widgetSelect: eventWidgetSelect } = useEventMarketTemplatesWidget();
  const marketTemplateData = useSelector(eventWidgetSelect.marketTemplateData) as
    | EventBasedMarketTemplatesView
    | MeetingBasedMarketTemplatesView;

  if ('events' in marketTemplateData)
    return marketTemplateData.events[market.eventId].eventStartTime;

  if ('races' in marketTemplateData) return marketTemplateData.races[market.raceId ?? ''].startTime;

  return market.marketTime;
}

const SelectionList: React.FC<PolymorphicComponentProps<'ul' | 'dl', SelectionListProps>> & {
  Item: typeof SelectionListItem;
  ShowMoreButton: typeof ShowMoreButton;
  Title: typeof Title;
} = ({ as, children, className, market, ...props }) => {
  const eventStartTime = useEventStartTime(market);

  if (!children) return null;

  const Component = as || 'ul';
  const childAs = as === 'dl' ? 'dd' : 'li';

  return (
    <Component {...props} className={classNames(styles.list, className)}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return null;
        if (typeof child.type === 'string') return child;

        switch (child.type.name) {
          case Title.name:
            return as === 'dl' ? child : null;
          case SelectionListItem.name:
            return React.cloneElement(child, {
              eventStartTime,
              market,
              ...child.props,
              as: childAs
            });
          default:
            return React.cloneElement(child, {
              ...child.props,
              as: childAs
            });
        }
      })}
    </Component>
  );
};

SelectionList.Item = SelectionListItem;
SelectionList.ShowMoreButton = ShowMoreButton;
SelectionList.Title = Title;

export default SelectionList;
